import React, { useContext, useEffect, useState } from "react";
import { exibirExtrato, proximoSlide, voltarSlide } from "./functions";
import "./Extrato.css";

import { ClientePageShell } from "../../../components/ClientePageShell";
import { CardExtrato } from "../../../components/Cards/CardExtrato";
import { LoadingButton } from "../../../components/loadingButtom";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";
import { TabelaExtrato } from "../../../components/Tabelas/TabelaExtrato";
import { SessaoExpiradaError } from "../../../services/authenticatedRequest";
import { baixarExtratoPdf } from "../../../services/statementReport";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Dropdown from "react-bootstrap/Dropdown";
import Slider from "react-slick";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import pt from "date-fns/locale/pt";
import {
    ServicoFinanceiroClienteContext,
    usuarioServicoFinanceiroContextType,
} from "../../../context/servicoFinanceiroCliente";
import {
    AiOutlineArrowLeft,
    AiOutlineArrowRight,
    AiOutlineDownload,
    AiOutlineReload,
    AiOutlineSearch,
} from "react-icons/ai";

registerLocale("pt", pt);

export interface RequisicaoExtrato {
    dataExecucao: Date;
    dataSolicitacao: Date;
    quantidadeCotas: number;
    tipo: "Aporte" | "Saque";
    status: "EmEspera" | "Efetuado" | "Rejeitado";
    valor: number;
    urlImagemComprovante: string;
}

export const Extrato = () => {
    const [dataInicial, setDataInicial] = useState<Date>(
        new Date(new Date().setDate(new Date().getDate() - 1))
    );
    const [dataFinal, setDataFinal] = useState<Date>(new Date());
    const [dataExecucao, setDataExecucao] = useState<boolean>(false);
    const [dados, setDados] = useState<Array<RequisicaoExtrato>>([]);
    const [modalDesconectar, setModalDesconectar] = useState<boolean>(false);
    const [erroPage, setErroPage] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [reloadPage, setReloadPage] = useState<boolean>(true);
    const [downloadingStatementReport, setDownloadingStatementReport] = useState(false);
    const [statementReportError, setStatementReportError] = useState("");

    const { reloadServicoFinanceiro, usuarioServicoFinanceiro } =
        useContext<usuarioServicoFinanceiroContextType>(ServicoFinanceiroClienteContext);

    useEffect(() => {
        exibirExtrato(
            dataInicial,
            dataFinal,
            setDados,
            setErroPage,
            setLoading,
            setModalDesconectar,
            usuarioServicoFinanceiro
        );
    }, [dataInicial, dataFinal, reloadPage, reloadServicoFinanceiro, usuarioServicoFinanceiro]);

    const settings = {
        dots: false,
        infinite: false,
        swipeToSlide: false,
        slidesToShow: 1,
        draggable: false,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: "0px",
        variableWidth: true,
        respondTo: "slider",
        rows: 2,
        slidesPerRow: 1,
        nextArrow: <div id="nextArrowSlide" style={{ display: "none" }} />,
        prevArrow: <div id="prevArrowSlide" style={{ display: "none" }} />,
    };

    const handleReload = () => setReloadPage((currentValue) => !currentValue);
    const colSpan = usuarioServicoFinanceiro.idServicoFinanceiro !== 3 ? 6 : 5;

    const handleDownloadStatementReport = async () => {
        setDownloadingStatementReport(true);
        setStatementReportError("");

        try {
            const { blob, fileName } = await baixarExtratoPdf({
                dataInicial,
                dataFinal,
                idServicoFinanceiro: usuarioServicoFinanceiro.idServicoFinanceiro,
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } catch (error) {
            if (error instanceof SessaoExpiradaError) {
                setModalDesconectar(true);
            } else {
                setStatementReportError("Não foi possível gerar o PDF do extrato.");
            }
        } finally {
            setDownloadingStatementReport(false);
        }
    };

    return (
        <>
            <ClientePageShell
                className="extrato-page"
                eyebrow="Histórico"
                title="Extrato"
                subtitle="Consulte movimentações, status de operações e comprovantes por período."
            >
                <section className="cliente-page-panel extrato-filter-panel">
                    <div className="cliente-page-section-header">
                        <div>
                            <p>Período</p>
                            <h2>Selecione as datas</h2>
                        </div>
                        <span>Pesquise o histórico ou gere o PDF do extrato para o período selecionado.</span>
                    </div>

                    <div className="extrato-filter-row">
                        <label>
                            <span>Data inicial</span>
                            <DatePicker
                                className="inputData"
                                maxDate={new Date(new Date().setDate(new Date().getDate() - 1))}
                                dateFormat="dd/MM/yyyy"
                                locale="pt"
                                selected={dataInicial}
                                onChange={(dataInicio: Date) => setDataInicial(dataInicio)}
                            />
                        </label>
                        <label>
                            <span>Data final</span>
                            <DatePicker
                                className="inputData"
                                maxDate={new Date(new Date().setDate(new Date().getDate()))}
                                minDate={dataInicial}
                                dateFormat="dd/MM/yyyy"
                                locale="pt"
                                selected={dataFinal}
                                onChange={(dataFim: Date) => setDataFinal(dataFim)}
                            />
                        </label>
                        <button
                            className="cliente-page-icon-button"
                            type="button"
                            onClick={handleReload}
                            aria-label="Pesquisar extrato"
                            title="Pesquisar extrato"
                        >
                            <AiOutlineSearch />
                        </button>
                        <button
                            className="cliente-page-button extrato-pdf-button"
                            type="button"
                            disabled={downloadingStatementReport}
                            onClick={handleDownloadStatementReport}
                        >
                            <AiOutlineDownload />
                            {downloadingStatementReport ? "Gerando..." : "Gerar PDF"}
                        </button>
                    </div>
                    {statementReportError && (
                        <small className="extrato-statement-error">
                            {statementReportError}
                        </small>
                    )}
                </section>

                <section className="cliente-page-panel mobileExtrato extrato-mobile-panel">
                    <div className="divBotoesExtratoMobile">
                        <button className="cliente-page-icon-button" type="button" onClick={() => voltarSlide()} aria-label="Voltar">
                            <AiOutlineArrowLeft className="iconButtonExtrato" />
                        </button>
                        <button className="cliente-page-icon-button" type="button" onClick={() => proximoSlide()} aria-label="Avançar">
                            <AiOutlineArrowRight className="iconButtonExtrato" />
                        </button>
                    </div>

                    <div className="boxSlideExtrato">
                        {erroPage ? (
                            <div className="cliente-page-empty">
                                <span className="formatacaoLoadingExtrato">
                                    Ocorreu um erro. Clique no botão para tentar novamente.
                                </span>
                                <button className="cliente-page-button secondary" type="button" onClick={handleReload}>
                                    <AiOutlineReload className="iconButtonExtrato" />
                                    Recarregar
                                </button>
                            </div>
                        ) : loading ? (
                            <span className="formatacaoLoadingExtrato">
                                <LoadingButton />
                            </span>
                        ) : (
                            <Slider {...settings}>
                                {dados.length !== 0 ? (
                                    dados.map((value: RequisicaoExtrato) => (
                                        <CardExtrato
                                            key={value.dataSolicitacao.toString() + value.valor}
                                            dados={value}
                                            dataExecucao={dataExecucao}
                                        />
                                    ))
                                ) : (
                                    <span className="extrato-empty-slide">Nenhum registro encontrado no momento.</span>
                                )}
                            </Slider>
                        )}
                    </div>
                </section>

                <section className="cliente-page-panel desktopExtrato extrato-table-panel">
                    <div className="cliente-page-section-header">
                        <div>
                            <p>Transações</p>
                            <h2>Extrato de transações</h2>
                        </div>
                    </div>

                    <div className="extrato-table-scroll">
                        <table id="tblTransacoes" className="tabela extrato-table">
                            <thead>
                                <tr className="textPeriodoMobile">
                                    <th className="table-secondary tableTitulo tableData colunas">
                                        <strong>Status</strong>
                                    </th>
                                    <th className="table-secondary tableTitulo tableData colunas select-data">
                                        <Dropdown>
                                            <Dropdown.Toggle variant="transparent" id="dropdown-basic">
                                                <strong>{dataExecucao ? "DATA EXECUÇÃO" : "DATA SOLICITAÇÃO"}</strong>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => setDataExecucao(false)}>
                                                    Data Solicitação
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setDataExecucao(true)}>
                                                    Data Execução
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </th>
                                    <th className="table-secondary tableTitulo tableTipo colunas">
                                        <strong>Tipo</strong>
                                    </th>
                                    {usuarioServicoFinanceiro.idServicoFinanceiro !== 3 && (
                                        <th className="table-secondary tableTitulo tableCotas colunas">
                                            <strong>Cotas</strong>
                                        </th>
                                    )}
                                    <th className="table-secondary tableTitulo tableOperacao colunas">
                                        <strong>Valor Operação</strong>
                                    </th>
                                    <th id="colunaComprovante" className="table-secondary tableTitulo tableOperacao colunas">
                                        <strong>Ver Comprovante</strong>
                                    </th>
                                </tr>
                            </thead>
                            {erroPage ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={colSpan}>
                                            <span>Ocorreu um erro. Por favor tente novamente.</span>
                                            <button className="extrato-inline-reload" type="button" onClick={handleReload}>
                                                <AiOutlineReload />
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            ) : loading ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={colSpan}>
                                            <span className="formatacaoLoadingExtrato">
                                                <LoadingButton />
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            ) : dados.length !== 0 ? (
                                dados.map((value: RequisicaoExtrato) => (
                                    <tbody key={value.dataSolicitacao.toString() + value.valor}>
                                        <TabelaExtrato
                                            fonteBold={true}
                                            dados={value}
                                            servicoFinanceiro={usuarioServicoFinanceiro.idServicoFinanceiro}
                                            dataExecucao={dataExecucao}
                                        />
                                    </tbody>
                                ))
                            ) : (
                                <tbody>
                                    <tr>
                                        <td colSpan={colSpan}>
                                            <span className="formatacaoLoadingExtrato">
                                                Nenhum dado encontrado no momento para o período.
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            )}
                        </table>
                    </div>
                </section>
            </ClientePageShell>
            <ModalDesconectar modal={modalDesconectar} />
        </>
    );
};
